import { useState } from "react";
import { ArrowLeft, ChevronLeft, ChevronRight, ExternalLink } from "lucide-react";
import { Article, Slide } from "@/hooks/useKnowledgeBase";
import { linkifyWhatsApp } from "@/lib/linkifyWhatsApp";

interface KnowledgeArticleViewProps {
  article: Article;
  onBack: () => void;
}

/**
 * Carrusel de pasos con captura (secciones `type: "slider"`). Se navega de a un paso
 * porque cada imagen es una captura de pantalla completa: mostrarlas apiladas hacía el
 * artículo interminable.
 */
function SliderSection({ title, slides }: { title: string; slides: Slide[] }) {
  const [current, setCurrent] = useState(0);
  const slide = slides[current];
  const isFirst = current === 0;
  const isLast = current === slides.length - 1;

  return (
    <div className="rounded-xl border border-white/15 bg-white/8 p-4">
      <p className="font-semibold text-white text-base mb-3">{title}</p>

      <img
        src={slide.image}
        alt={slide.title}
        loading="lazy"
        className="w-full rounded-lg border border-white/10 bg-black/20"
      />

      <p className="mt-3 text-sm font-semibold text-psi-lime">{slide.title}</p>
      <p className="mt-1 text-sm text-white/70">{linkifyWhatsApp(slide.text)}</p>

      <div className="mt-4 flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => setCurrent((i) => Math.max(0, i - 1))}
          disabled={isFirst}
          aria-label="Paso anterior"
          className="flex items-center gap-1 rounded-lg border border-white/15 px-3 py-1.5 text-xs text-white/80 transition enabled:hover:bg-white/10 disabled:opacity-40"
        >
          <ChevronLeft className="h-4 w-4" />
          Anterior
        </button>

        <div className="flex items-center gap-1.5" role="tablist" aria-label="Pasos">
          {slides.map((s, i) => (
            <button
              key={i}
              type="button"
              role="tab"
              aria-selected={i === current}
              aria-label={`Ir a ${s.title}`}
              onClick={() => setCurrent(i)}
              className={`h-1.5 rounded-full transition-all ${
                i === current ? "w-5 bg-psi-lime" : "w-1.5 bg-white/25 hover:bg-white/40"
              }`}
            />
          ))}
        </div>

        <button
          type="button"
          onClick={() => setCurrent((i) => Math.min(slides.length - 1, i + 1))}
          disabled={isLast}
          aria-label="Paso siguiente"
          className="flex items-center gap-1 rounded-lg border border-white/15 px-3 py-1.5 text-xs text-white/80 transition enabled:hover:bg-white/10 disabled:opacity-40"
        >
          Siguiente
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

export function KnowledgeArticleView({ article, onBack }: KnowledgeArticleViewProps) {
  const renderBoldText = (text: string) => {
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, i) => {
      if (part.startsWith("**") && part.endsWith("**")) {
        return <strong key={i}>{part.slice(2, -2)}</strong>;
      }
      return linkifyWhatsApp(part);
    });
  };

  const renderSection = (section: typeof article.content.sections[0], index: number): React.ReactNode => {
    switch (section.type) {
      case "text":
        return (
          <div key={index} className={`rounded-xl border p-4 ${section.highlight ? "border-psi-lime/30 bg-psi-lime/5" : "border-white/15 bg-white/8"}`}>
            <p className="font-semibold text-white text-base mb-2">{section.title}</p>
            <p className="text-sm text-white/70">{linkifyWhatsApp(section.content as string)}</p>
          </div>
        );

      case "list":
        return (
          <div key={index} className={`rounded-xl border p-4 ${section.highlight ? "border-psi-lime/30 bg-psi-lime/5" : "border-white/15 bg-white/8"}`}>
            <p className="font-semibold text-white text-base mb-1">{section.title}</p>
            {section.subtitle && <p className="text-sm text-white/60 mb-2">{section.subtitle}</p>}
            <ul className="space-y-2 text-sm">
              {(section.content as string[]).map((item, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="text-psi-lime mt-0.5">•</span>
                  <span className="text-white/70">
                    {item.includes(": ") ? (
                      <>
                        <strong className="text-white">{item.split(": ")[0]}:</strong>{" "}
                        {linkifyWhatsApp(item.split(": ").slice(1).join(": "))}
                      </>
                    ) : (
                      linkifyWhatsApp(item)
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        );

      case "steps":
        return (
          <div key={index} className="rounded-xl border border-white/15 bg-white/8 p-4">
            <p className="font-semibold text-white text-base mb-3">{section.title}</p>
            <ol className="space-y-3 text-sm">
              {(section.content as string[]).map((step, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-psi-lime/10 text-psi-lime text-xs font-semibold flex items-center justify-center">
                    {i + 1}
                  </span>
                  <span className="text-white/70 pt-0.5">{linkifyWhatsApp(step)}</span>
                </li>
              ))}
            </ol>
          </div>
        );

      case "warning":
        return (
          <div key={index} className="rounded-xl border border-red-400/40 bg-red-500/10 p-4">
            <p className="font-bold text-red-300 flex items-center gap-2 mb-1 text-base">⚠️ {section.title}</p>
            <p className="text-sm text-white/80 font-medium">{renderBoldText(section.content as string)}</p>
          </div>
        );

      case "tip":
        return (
          <div key={index} className="rounded-xl border border-psi-lime/30 bg-psi-lime/5 p-4">
            <p className="font-semibold text-psi-lime flex items-center gap-2 mb-1 text-base">💡 {section.title}</p>
            <p className="text-sm text-white/70">{linkifyWhatsApp(section.content as string)}</p>
          </div>
        );

      case "contact": {
        const lines = (section.content as string).split("\n");
        return (
          <div key={index} className="rounded-xl border border-psi-lime/30 bg-psi-lime/5 p-4">
            <p className="font-semibold text-white flex items-center gap-2 mb-2 text-base">📞 {section.title}</p>
            <div className="space-y-1 text-sm">
              {lines.map((line, i) => {
                const isHighlight = line.startsWith("WhatsApp:") || line.startsWith("Email:");
                return (
                  <p key={i} className={isHighlight ? "font-semibold text-psi-lime" : "text-white/70"}>
                    {linkifyWhatsApp(line)}
                  </p>
                );
              })}
            </div>
          </div>
        );
      }

      case "slider":
        if (!section.slides?.length) return null;
        return <SliderSection key={index} title={section.title} slides={section.slides} />;

      case "columns":
        if (!section.columns) return null;
        return (
          <div key={index} className="space-y-4">
            {section.title && (
              <h3 className="text-lg font-bold text-white text-center">{section.title}</h3>
            )}
            <div className={`grid grid-cols-1 gap-4 ${section.columns!.length === 3 ? "md:grid-cols-3" : "md:grid-cols-2"}`}>
              {section.columns.map((column, colIndex) => (
                <div key={colIndex} className="space-y-4 flex flex-col">
                  {column.map((subSection, subIndex) => (
                    <div key={subIndex} className="flex-1 flex flex-col [&>*]:h-full">
                      {renderSection(subSection, subIndex)}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-6 pt-2">
      <div className="space-y-3">
        <button
          className="flex items-center gap-1 text-sm px-3 py-1.5 rounded-lg bg-[hsl(0,0%,85%)] hover:bg-[hsl(0,0%,78%)] text-black transition-colors"
          onClick={onBack}
        >
          <ArrowLeft className="w-4 h-4" />
          Volver
        </button>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-psi-lime/10 flex items-center justify-center">
            <article.icon className="w-5 h-5 text-psi-lime" />
          </div>
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-white">{article.title}</h2>
            <p className="text-white/60 text-sm">{article.description}</p>
          </div>
        </div>
      </div>

      {article.content.intro && (
        <p className="text-sm text-white/70">{article.content.intro}</p>
      )}

      <div className="space-y-4">
        {article.content.sections.map((section, index) => renderSection(section, index))}
      </div>

      {article.content.externalLinks && article.content.externalLinks.length > 0 && (
        <div className="space-y-2">
          {article.content.externalLinks.map((link, index) => (
            <a
              key={index}
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full py-2.5 px-4 rounded-full bg-psi-lime hover:bg-psi-lime/80 text-[#2d1a5e] text-sm font-semibold transition-colors"
            >
              {link.icon} {link.label}
              <ExternalLink className="w-4 h-4" />
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
